import type { ReactNode } from "react";
import { cn } from "@/lib/cn";
import { Button } from "./Button";

/**
 * Loading, empty and error — the three states every screen in this console
 * has, kept together because the mistake they prevent is treating them as
 * one. An empty queue is GOOD NEWS and should look calm; a failed request is
 * not, and should not look the same.
 */

export function Skeleton({ className }: { className?: string }) {
  return (
    <div
      className={cn(
        "rounded-control bg-surface-2 animate-sweep",
        "bg-[linear-gradient(90deg,transparent_0%,rgb(var(--color-surface-3))_50%,transparent_100%)]",
        "bg-[length:200%_100%]",
        className,
      )}
    />
  );
}

export function TableSkeleton({ rows = 6 }: { rows?: number }) {
  return (
    <div className="flex flex-col gap-1.5 p-3">
      {Array.from({ length: rows }, (_, i) => (
        <Skeleton
          key={i}
          className="h-6"
          // A little variation, so it reads as content arriving rather than
          // as a loading graphic.
        />
      ))}
    </div>
  );
}

export function EmptyState({
  title,
  hint,
}: {
  title: string;
  hint?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-1 py-12 px-6 text-center">
      <p className="text-[13px] font-medium text-ink-2">{title}</p>
      {hint ? (
        <p className="text-micro text-muted max-w-[46ch]">{hint}</p>
      ) : null}
    </div>
  );
}

export function ErrorState({
  message = "Could not load that.",
  onRetry,
}: {
  message?: string;
  onRetry?: () => void;
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 py-10 px-6 text-center">
      <p className="text-[13px] font-medium text-danger">{message}</p>
      {onRetry ? (
        <Button onClick={onRetry} variant="secondary">
          Try again
        </Button>
      ) : null}
    </div>
  );
}
